import { useState, useEffect, useContext } from "react";
import { GlobalContext } from "./GlobalContext";
import { emitter } from "./UtilityFunctions";
import { isPasskeyPromptOpen } from "./PasskeyPrompt";
import { isWelcomeTourPromptOpen } from "./WelcomeTourPrompt";
import { useSetupCompletion } from "./hooks/useSetupCompletion";
import SetupWizardModal from "./SetupWizardModal";

/**
 * The setup checklist, put in front of whoever can act on it, once a session.
 *
 * WHY A PROMPT AND NOT JUST THE DASHBOARD BANNER. The thing this exists to
 * fix is that nobody goes back to Settings — so a reminder that waits to be
 * noticed is a reminder aimed at the one behaviour we already know does not
 * happen. This arrives.
 *
 * WHY PER SESSION AND NOT PER USER FOREVER. sessionStorage, so "Remind me next
 * login" means exactly that: closing the browser brings it back, switching
 * tabs does not. An answer remembered forever would make this a one-time offer
 * for a job that takes several sittings, and an answer remembered nowhere
 * would make it a modal you dismiss every time you glance at the dashboard.
 * This is the rule the CMS checklist uses (useOnboarding.js) and it is the
 * reason it still gets acted on in week three.
 *
 * WHY IT DISAPPEARS AT 100%. There is nothing to say, and a congratulatory
 * modal at every login is how people learn to click through modals without
 * reading them — including the ones that matter.
 *
 * THE PROMPT QUEUE. Three prompts can now want the screen at first login. The
 * rule, unchanged from WelcomeTourPrompt: never during the undismissable
 * first-login password change, and never on top of another prompt — wait for
 * "prompt:closed" and try again. This one goes last of the three deliberately.
 * The passkey offer and the tour are about using the app at all; this is a
 * chore list, and putting a chore list in front of someone before they have
 * seen the app is how it gets dismissed unread.
 */

//Not user-scoped, unlike the passkey and tour flags. Those live in
//localStorage, which a second person on a shared office machine inherits, so
//they must be keyed by user. This is sessionStorage: it belongs to one tab and
//dies with it, and a tab cannot be two people. Keeping it constant also means
//the QA suite can pre-answer it without knowing who is logged in.
const SESSION_KEY = "setup-prompt-answered";

export default function SetupPrompt() {
    const { can, UserID, userData } = useContext(GlobalContext);
    const allowed = can("settings.edit");
    const firstLogin = userData?.FirstLogin;

    const [visible, setVisible] = useState(false);
    const { data } = useSetupCompletion({ enabled: allowed });

    const outstanding = data ? data.applicable - data.done : 0;

    useEffect(() => {
        if (!allowed || !UserID || firstLogin) return;
        //Nothing outstanding, or the summary has not arrived (or failed) — in
        //both cases there is no list to show.
        if (!data || outstanding === 0) return;
        try {
            if (sessionStorage.getItem(SESSION_KEY) === "true") return;
        } catch {
            return;   //storage disabled: no way to remember an answer, so don't ask
        }

        let timer = null;
        const busy = () => isPasskeyPromptOpen() || isWelcomeTourPromptOpen();
        const answered = () => {
            //Re-read rather than trusting the check above: this component both
            //emits "prompt:closed" and listens for it, so its own close is
            //delivered to its own handler. Without this it would put itself
            //straight back on screen.
            try { return sessionStorage.getItem(SESSION_KEY) === "true"; } catch { return true; }
        };
        const show = () => {
            if (answered()) return;
            //One prompt at a time. If either of the other two is up, wait for
            //it to close and the "prompt:closed" retry below brings us back.
            if (busy()) return;
            //Re-checked when the timer FIRES, not only when it is set. With
            //three prompts the schedule-time check is no longer sufficient:
            //closing the passkey offer wakes this and the tour offer together,
            //and the tour's shorter delay means it is on screen before this
            //fires. Its own close then wakes us again.
            clearTimeout(timer);
            //Let the dashboard paint first, so the modal arrives over the app
            //rather than over a spinner — and land after the tour offer, which
            //waits 600ms, so a tie goes to the tour.
            timer = setTimeout(() => { if (!busy()) setVisible(true); }, 800);
        };

        show();
        emitter.on("prompt:closed", show);
        return () => {
            clearTimeout(timer);
            emitter.removeListener("prompt:closed", show);
        };
    }, [allowed, UserID, firstLogin, data, outstanding]);

    const close = () => {
        //Closing IS the answer — there is no separate "yes". Whether they
        //worked through the list or shut it, they have been told, and asking
        //again this session would be nagging rather than reminding.
        try { sessionStorage.setItem(SESSION_KEY, "true"); } catch { /* non-fatal */ }
        setVisible(false);
        emitter.emit("prompt:closed", "setup");
    };

    return (
        <SetupWizardModal
            open={visible}
            onClose={close}
            dismissLabel="Remind me next login"
        />
    );
}
